import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import ClearIcon from '@material-ui/icons/Clear';
import { SnackbarContext } from 'components';
import * as React from 'react';
import {
  LightspeedCatalogItem,
  LightspeedMappingService,
  LightspeedShopId,
  LightspeedShopState,
  LightspeedStateMenuItem,
} from 'services';

interface Props {
  shopId: LightspeedShopId;
  state: LightspeedShopState | null;
  itemsCatalog: LightspeedCatalogItem[];
  onChanged: () => void | Promise<void>;
}

interface CatalogOption {
  sku: string;
  label: string;
}

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    '& > * + *': { marginLeft: theme.spacing(2) },
  },
  search: { minWidth: 280 },
  filterCheck: {
    marginRight: 0,
  },
  emptyHint: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(2),
  },
  ok: { background: '#e8f5e9' },
  drift: { background: '#ffebee' },
  groupHeader: {
    background: '#f5f5f5',
    '& td': { fontWeight: 600 },
  },
  skuCell: { minWidth: 280 },
  rowMod: {
    background: '#fff3e0',
  },
}));

export const ItemsTab: React.FC<Props> = ({
  shopId,
  state,
  itemsCatalog,
  onChanged,
}) => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);

  const [filter, setFilter] = React.useState('');
  const [showOnlyUnmapped, setShowOnlyUnmapped] = React.useState(false);
  const [savingId, setSavingId] = React.useState<number | null>(null);

  const options: CatalogOption[] = React.useMemo(
    () =>
      itemsCatalog
        .filter((c): c is LightspeedCatalogItem & { sku: string } =>
          typeof c.sku === 'string' && c.sku !== '',
        )
        .map((c) => ({
          sku: String(c.sku),
          label:
            (typeof c.name === 'string' && c.name !== ''
              ? c.name
              : '(no name)') + ` · SKU ${c.sku}`,
        })),
    [itemsCatalog],
  );

  // Quick lookup: does this SKU exist in the latest snapshot?
  const validSkus = React.useMemo(
    () => new Set(options.map((o) => o.sku)),
    [options],
  );

  const filtered = React.useMemo(() => {
    if (!state) return [];
    const q = filter.trim().toLowerCase();
    return state.menuItems.filter((mi) => {
      if (showOnlyUnmapped && (mi.lightspeedSku || mi.lightspeedModifierId)) {
        return false;
      }
      if (q === '') return true;
      return (
        mi.title.toLowerCase().includes(q) ||
        mi.categoryName.toLowerCase().includes(q) ||
        mi.sectionName.toLowerCase().includes(q)
      );
    });
  }, [state, filter, showOnlyUnmapped]);

  async function saveSku(item: LightspeedStateMenuItem, sku: string | null) {
    try {
      setSavingId(item.id);
      await LightspeedMappingService.updateMenuItem(item.id, {
        shopId,
        lightspeed_sku: sku,
      });
      snackbar.success(
        sku ? `Linked "${item.title}" → SKU ${sku}` : `Cleared "${item.title}"`,
      );
      await onChanged();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setSavingId(null);
    }
  }

  if (!state) {
    return (
      <Typography className={classes.emptyHint}>
        Loading menu items for shop {shopId}…
      </Typography>
    );
  }

  const catalogReady = itemsCatalog.length > 0;

  return (
    <Box>
      <Box className={classes.toolbar}>
        <TextField
          variant="outlined"
          size="small"
          className={classes.search}
          placeholder="Search menu items…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <label>
          <input
            type="checkbox"
            checked={showOnlyUnmapped}
            onChange={(e) => setShowOnlyUnmapped(e.target.checked)}
          />{' '}
          Show unmapped only
        </label>
        <Box flex={1} />
        <Typography variant="body2" color="textSecondary">
          {state.menuItems.length} items in shop {shopId} ·{' '}
          {catalogReady
            ? `${options.length} SKUs available`
            : 'Paste an items catalog first'}
        </Typography>
      </Box>

      {!catalogReady && (
        <Paper className={classes.emptyHint} elevation={0}>
          No items catalog has been pasted for this shop yet. Use the{' '}
          <strong>Paste catalogs</strong> tab.
        </Paper>
      )}

      {catalogReady && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '35%' }}>Item</TableCell>
                <TableCell style={{ width: '20%' }}>Category</TableCell>
                <TableCell style={{ width: '10%' }}>Status</TableCell>
                <TableCell className={classes.skuCell}>
                  Lightspeed SKU
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((mi) => {
                const currentSku = mi.lightspeedSku;
                const hasModifier = !!mi.lightspeedModifierId;
                const driftBadge =
                  currentSku && !validSkus.has(currentSku) ? (
                    <Chip
                      size="small"
                      label="Drift — SKU not in catalog"
                      className={classes.drift}
                    />
                  ) : currentSku ? (
                    <Chip size="small" label="Mapped" className={classes.ok} />
                  ) : hasModifier ? (
                    <Chip size="small" label="Modifier" />
                  ) : (
                    <Chip size="small" label="Unmapped" />
                  );

                const selectedOpt = currentSku
                  ? options.find((o) => o.sku === currentSku) ?? {
                      sku: currentSku,
                      label: `${currentSku} (not in latest catalog)`,
                    }
                  : null;

                return (
                  <TableRow
                    key={mi.id}
                    className={hasModifier ? classes.rowMod : ''}
                  >
                    <TableCell>
                      <strong>{mi.title}</strong>
                      <br />
                      <Typography
                        variant="caption"
                        color="textSecondary"
                      >
                        id {mi.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{mi.sectionName}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {mi.categoryName}
                      </Typography>
                    </TableCell>
                    <TableCell>{driftBadge}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Autocomplete
                          style={{ flex: 1 }}
                          size="small"
                          options={options}
                          getOptionLabel={(o) => o.label}
                          getOptionSelected={(a, b) => a.sku === b.sku}
                          value={selectedOpt}
                          disabled={savingId === mi.id}
                          onChange={(_e: any, picked: CatalogOption | null) => {
                            const sku = picked ? picked.sku : null;
                            if (sku === currentSku) return;
                            saveSku(mi, sku);
                          }}
                          renderInput={(p) => (
                            <TextField
                              {...p}
                              variant="outlined"
                              placeholder={
                                hasModifier
                                  ? 'Setting an SKU will clear the modifier link'
                                  : 'Pick a Lightspeed SKU'
                              }
                            />
                          )}
                        />
                        {currentSku && (
                          <IconButton
                            size="small"
                            disabled={savingId === mi.id}
                            onClick={() => saveSku(mi, null)}
                            aria-label="Clear mapping"
                          >
                            <ClearIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography className={classes.emptyHint}>
                      No items match the current filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};
